import { portilloLiftIdentity } from "./portillo-lifts";
import type { PlannerGraph } from "./graph-types";
import type { PlannerPreferences } from "./preferences";

/** Operator guidance is a suitability floor, independent of terrain grade,
 * lift opening, pass ownership and the rider's willingness to push harder. */
export type LiftSuitability = {
  policyId: "portillo-va-et-vient-v1";
  sourceLiftId: string;
  minimumAbility: "expert";
  sourceUrl: string;
  reviewedOn: string;
};

export const EXPERT_LIFT_NOTE = "Ski Portillo recommends this va-et-vient lift for expert skiers only. Routes include it only when your ski ability is set to expert. Follow the operator's instructions and current closures.";
const SOURCE_URL = "https://skiportillo.com/en/our-mountain/ski-slopes-skiable-terrain/";
const REVIEWED_ON = "2026-09-08";
// Exact reviewed OSM ways and their frozen OpenSkiMap content identities. No
// name, lift-type or nearby-geometry inference is permitted here.
const IDENTITIES = [
  ["way/258950664", "26d55fd50014082fe90c2d277d9ba68ae8fe11f8"], // Condor
  ["way/258950816", "5b82cf16b550590d4c5846b9da50399342905240"], // Roca Jack
  ["way/258950817", "7f67a68a7ea2cc2e7fcdfa2bd223cf9b3fec8a16"], // Cara Cara
  ["way/258950665", "f7fba69c83fb498d0e5dabccdf17dc64ea4dedda"], // Las Vizcachas
] as const;
const POLICIES = new Map<string, LiftSuitability>();
for (const [way, contentId] of IDENTITIES) {
  const policy: LiftSuitability = { policyId: "portillo-va-et-vient-v1", sourceLiftId: way, minimumAbility: "expert", sourceUrl: SOURCE_URL, reviewedOn: REVIEWED_ON };
  POLICIES.set(way, policy);
  POLICIES.set(contentId, policy);
}

export function liftSuitabilityForIdentity(areaSlug: string, sourceId: string | null | undefined): LiftSuitability | undefined {
  return areaSlug === "portillo" && sourceId ? POLICIES.get(sourceId) : undefined;
}

/** Validate the version and exact evidence, including old or untrusted saved
 * markers. Reviewed-on dates record the check, not an operating season. */
export function validLiftSuitability(value: unknown): value is LiftSuitability {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const policy = value as Record<string, unknown>;
  const expected = typeof policy.sourceLiftId === "string" ? POLICIES.get(policy.sourceLiftId) : undefined;
  return !!expected && Object.entries(expected).every(([key, expectedValue]) => policy[key] === expectedValue);
}

export function liftSuitabilityAllows(policy: unknown, ability: PlannerPreferences["ability"] | undefined): boolean {
  return validLiftSuitability(policy) && ability === "expert";
}

/** Used by ingestion and runtime assembly. Runtime resolves active catalogue
 * identities again so a legacy edge missing its marker cannot bypass the rule. */
export function withLiftSuitability(graph: PlannerGraph, areaSlug: string, rows: readonly { id: string; osmId: string | null | undefined }[]): PlannerGraph {
  if (areaSlug !== "portillo") return graph;
  const byId = new Map(rows.map(row => [row.id, row.osmId]));
  return { ...graph, edges: graph.edges.map(edge => {
    if (edge.kind !== "lift") return edge;
    const sourceId = byId.get(edge.id);
    const suitability = liftSuitabilityForIdentity(areaSlug, sourceId);
    return { ...edge, suitabilityChecked: !!portilloLiftIdentity(sourceId), ...(suitability ? { suitability } : {}) };
  }) };
}
